/**
 * The citizens as the city loads them: one file, baked from the figures made in Blender
 * (tools/characters/bake_citizens.mjs, docs/CITIZENS.md). All figures share one skeleton and its
 * clips, so the clips are held once, as a table of bone poses, and a figure is its vertices, the
 * bones each follows, and its triangles with their colours. Figures face +X with their feet on
 * y = 0, as everything in life/ does.
 */

/** A clip's rows in the pose table: `rows` of them from `row`, then its first row once more. */
export interface CitizenClip {
  readonly row: number;
  readonly rows: number;
  readonly seconds: number;
  /** The ground one cycle of a walk covers; a figure moved so far a cycle does not slide. */
  readonly strideM?: number;
}

/** One figure at one level of detail. */
export interface CitizenLod {
  readonly vertices: number;
  readonly triangles: number;
  /** Three 16-bit counts of `positionStepM` a vertex. */
  readonly places: Int16Array;
  /** Four bones a vertex, and their weights as bytes that sum to 255. */
  readonly joints: Uint8Array;
  readonly weights: Uint8Array;
  /** Six numbers a triangle: its three vertices, then its linear colour in 16 bits a channel. */
  readonly faces: Uint16Array;
}

export interface CitizenFigure {
  readonly name: string;
  readonly trade: string;
  readonly sex: 'm' | 'f';
  readonly build: string;
  /** Asked for by name (tools/characters/citizen.py), not one of a trade's three. */
  readonly named: boolean;
  /** The clips that suit it: standing, going, and at its work. */
  readonly clips: { readonly idle: string; readonly walk: string; readonly work: string };
  readonly lods: readonly CitizenLod[];
}

export interface CitizenAsset {
  readonly bones: readonly string[];
  readonly rowsPerSecond: number;
  readonly positionStepM: number;
  /** Eight numbers a bone a row: its turn as a quaternion, its shift, and one unused. */
  readonly poses: Float32Array;
  readonly poseRows: number;
  readonly clips: Readonly<Record<string, CitizenClip>>;
  readonly figures: readonly CitizenFigure[];
}

const MAGIC = 'AMSCIT01';
const VERSION = 1;
const LEAD_BYTES = 12;
export const POSE_FLOATS = 8;

interface Header {
  readonly version: number;
  readonly bones: readonly string[];
  readonly rowsPerSecond: number;
  readonly positionStepM: number;
  readonly poses: { readonly offset: number; readonly rows: number };
  readonly clips: Readonly<Record<string, CitizenClip>>;
  readonly figures: readonly (Omit<CitizenFigure, 'lods'> & {
    readonly lods: readonly {
      readonly vertices: number;
      readonly triangles: number;
      readonly offset: number;
    }[];
  })[];
}

const padded = (length: number): number => length + ((4 - (length % 4)) % 4);

/** Reads the file; throws, saying what is wrong, on anything that is not a whole citizens file. */
export function parseCitizens(buffer: ArrayBuffer): CitizenAsset {
  const lead = new DataView(buffer, 0, Math.min(LEAD_BYTES, buffer.byteLength));
  const magic = Array.from({ length: Math.min(MAGIC.length, lead.byteLength) }, (_, i) =>
    String.fromCharCode(lead.getUint8(i))
  ).join('');
  if (magic !== MAGIC) throw new Error('citizens: not a citizens file');
  const headerBytes = lead.getUint32(8, true);
  const payload = LEAD_BYTES + padded(headerBytes);
  if (payload > buffer.byteLength) throw new Error('citizens: the file is cut short');
  const header = JSON.parse(
    new TextDecoder().decode(new Uint8Array(buffer, LEAD_BYTES, headerBytes))
  ) as Header;
  if (header.version !== VERSION)
    throw new Error(`citizens: version ${String(header.version)}, not ${String(VERSION)}`);

  const within = (offset: number, bytes: number): number => {
    if (payload + offset + bytes > buffer.byteLength)
      throw new Error('citizens: the file is cut short');
    return payload + offset;
  };
  const poseFloats = header.poses.rows * header.bones.length * POSE_FLOATS;
  const poses = new Float32Array(buffer, within(header.poses.offset, poseFloats * 4), poseFloats);
  for (const [name, clip] of Object.entries(header.clips))
    if (clip.row + clip.rows >= header.poses.rows)
      throw new Error(`citizens: the clip ${name} runs past the pose table`);

  const figures = header.figures.map((figure): CitizenFigure => ({
    ...figure,
    lods: figure.lods.map(({ vertices, triangles, offset }): CitizenLod => {
      // The places are padded to a whole number of 32-bit words.
      const placeCounts = vertices * 3 + (vertices % 2);
      const at = within(offset, placeCounts * 2 + vertices * 8 + triangles * 12);
      return {
        vertices,
        triangles,
        places: new Int16Array(buffer, at, vertices * 3),
        joints: new Uint8Array(buffer, at + placeCounts * 2, vertices * 4),
        weights: new Uint8Array(buffer, at + placeCounts * 2 + vertices * 4, vertices * 4),
        faces: new Uint16Array(buffer, at + placeCounts * 2 + vertices * 8, triangles * 6),
      };
    }),
  }));
  return {
    bones: header.bones,
    rowsPerSecond: header.rowsPerSecond,
    positionStepM: header.positionStepM,
    poses,
    poseRows: header.poses.rows,
    clips: header.clips,
    figures,
  };
}
